'use client';

import { useEffect } from 'react';
import { Study } from '@/lib/study';
import { T } from '@/lib/tokens';

type Props = {
  study: Study;
  onClose: () => void;
};

export function ParticipantPreviewModal({ study, onClose }: Props) {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const visibleBrief = study.brief.filter((s) => s.title && s.body);
  const researcherLine = [study.researcher, study.affiliation].filter(Boolean).join(', ');

  const downloadPdf = () => {
    const briefHtml = visibleBrief.map((s, i) => `
      <div style="padding:18px 24px;border-bottom:${i < visibleBrief.length - 1 ? '1px solid oklch(87% 0.022 75)' : 'none'};display:flex;gap:16px;">
        <span style="font-family:'DM Serif Display',serif;font-size:14px;color:oklch(62% 0.13 48);letter-spacing:0.05em;min-width:28px;padding-top:2px;">${String(i + 1).padStart(2, '0')}</span>
        <div>
          <div style="font-size:15px;font-weight:600;color:oklch(18% 0.018 60);margin-bottom:6px;">${s.title}</div>
          <div style="font-size:14px;color:oklch(38% 0.022 60);line-height:1.65;">${s.body}</div>
        </div>
      </div>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Participant Information Sheet</title>
      <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet">
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'DM Sans',sans-serif;background:oklch(97% 0.012 75);padding:40px 24px;color:oklch(18% 0.018 60);}
        .page{max-width:620px;margin:0 auto;display:flex;flex-direction:column;gap:24px;}
        @media print{body{padding:24px 16px;}}
      </style>
    </head><body><div class="page">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="width:8px;height:8px;border-radius:50%;background:oklch(62% 0.13 48);"></div>
        <span style="font-family:'DM Serif Display',serif;font-size:16px;">AI Interviewer</span>
      </div>
      <div>
        <div style="font-size:12px;letter-spacing:0.14em;text-transform:uppercase;color:oklch(62% 0.13 48);font-weight:600;margin-bottom:10px;">Participant information</div>
        <h1 style="font-family:'DM Serif Display',serif;font-size:34px;line-height:1.15;letter-spacing:-0.02em;margin-bottom:12px;">${study.goal || 'Study title'}</h1>
        <p style="font-size:16px;line-height:1.6;color:oklch(38% 0.022 60);">Thanks for considering this study. Please read through the information below before you decide to take part.</p>
      </div>
      <div style="background:oklch(99% 0.005 75);border:1px solid oklch(87% 0.022 75);border-radius:12px;padding:16px 20px;display:grid;grid-template-columns:repeat(3,1fr);gap:16px;">
        ${[['⏱','Length',`~${study.duration} min`],['🎙','Format','Voice with AI'],['🎁','Compensation',study.compensation||'—']].map(([icon,label,value])=>`
        <div><div style="font-size:18px;margin-bottom:4px;">${icon}</div><div style="font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:oklch(58% 0.018 60);font-weight:600;">${label}</div><div style="font-size:15px;font-weight:500;">${value}</div></div>`).join('')}
      </div>
      ${visibleBrief.length > 0 ? `<div style="background:oklch(99% 0.005 75);border:1px solid oklch(87% 0.022 75);border-radius:12px;overflow:hidden;">${briefHtml}</div>` : ''}
      ${researcherLine || study.contactEmail ? `
      <div style="background:oklch(92% 0.04 65);border:1px solid oklch(78% 0.07 55);border-radius:12px;padding:16px 20px;">
        <div style="font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:oklch(58% 0.018 60);font-weight:600;margin-bottom:6px;">Research team</div>
        ${researcherLine ? `<div style="font-size:15px;font-weight:500;margin-bottom:4px;">${researcherLine}</div>` : ''}
        ${study.contactEmail ? `<div style="font-size:14px;color:oklch(38% 0.022 60);">Questions? Email <span style="color:oklch(48% 0.14 45);text-decoration:underline;">${study.contactEmail}</span></div>` : ''}
        ${study.irb ? `<div style="font-size:12px;color:oklch(58% 0.018 60);font-style:italic;margin-top:6px;">${study.irb}</div>` : ''}
      </div>` : ''}
      <div style="background:oklch(99% 0.005 75);border:1px solid oklch(87% 0.022 75);border-radius:12px;padding:18px 22px;">
        <div style="font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:oklch(58% 0.018 60);font-weight:600;margin-bottom:12px;">Your consent</div>
        ${["I'm 18 or older and I'm taking part in this study voluntarily.","I've read the information above and understand that my conversation will be recorded and transcribed for research purposes.","I understand I can stop at any time without giving a reason."].map(l=>`
        <div style="display:flex;gap:10px;padding:8px 0;align-items:flex-start;">
          <div style="width:18px;height:18px;border-radius:4px;border:1.5px solid oklch(87% 0.022 75);flex-shrink:0;margin-top:1px;"></div>
          <span style="font-size:14px;line-height:1.55;color:oklch(18% 0.018 60);">${l}</span>
        </div>`).join('')}
      </div>
    </div><script>window.onload=()=>{window.print();}<\/script></body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'oklch(12% 0.01 60 / 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        overflowY: 'auto',
        padding: '32px 24px 48px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 660, display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* Preview banner */}
        <div style={{
          background: T.espresso, color: T.cream,
          borderRadius: '12px 12px 0 0',
          padding: '10px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600,
        }}>
          <span>Participant preview</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={downloadPdf}
              style={{ background: 'oklch(99% 0.005 75 / 0.12)', border: '1px solid oklch(99% 0.005 75 / 0.2)', borderRadius: 6, cursor: 'pointer', color: T.cream, fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', padding: '5px 12px', fontFamily: 'inherit' }}
            >
              Download PDF
            </button>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.cream, fontSize: 20, lineHeight: 1, padding: '0 0 0 4px', opacity: 0.7 }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Participant page */}
        <div style={{
          background: T.cream,
          borderRadius: '0 0 12px 12px',
          padding: '40px 40px 48px',
          display: 'flex', flexDirection: 'column', gap: 28,
        }}>

          {/* Brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.terra, boxShadow: `0 0 0 3px oklch(62% 0.13 48 / 0.12)` }} />
            <span style={{ fontFamily: 'DM Serif Display, serif', fontSize: 16, color: T.espresso, letterSpacing: '-0.01em' }}>
              AI Interviewer
            </span>
          </div>

          {/* Title area */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.terra, fontWeight: 600 }}>
              Participant information
            </span>
            <h1 style={{ fontFamily: 'DM Serif Display, serif', fontSize: 36, lineHeight: 1.15, color: T.espresso, letterSpacing: '-0.02em', margin: 0 }}>
              {study.goal || <span style={{ opacity: 0.35 }}>Your study title</span>}
            </h1>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: T.espressoMid, margin: 0 }}>
              Thanks for considering this study. Please read through the information below before you decide to take part. You can reach out to the research team anytime if anything's unclear.
            </p>
          </div>

          {/* Quick facts */}
          <div style={{
            background: T.white,
            border: `1px solid ${T.creamBorder}`,
            borderRadius: 16,
            padding: '18px 22px',
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18,
          }}>
            {[
              { icon: '⏱', label: 'Length', value: `~${study.duration} min` },
              { icon: '🎙', label: 'Format', value: 'Voice with AI' },
              { icon: '🎁', label: 'Compensation', value: study.compensation || <span style={{ opacity: 0.4, fontStyle: 'italic' }}>Not set</span> },
            ].map(({ icon, label, value }) => (
              <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 18, marginBottom: 2 }}>{icon}</span>
                <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.espressoLight, fontWeight: 600 }}>{label}</span>
                <span style={{ fontSize: 15, color: T.espresso, fontWeight: 500 }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Brief sections */}
          {visibleBrief.length > 0 && (
            <div style={{
              background: T.white,
              border: `1px solid ${T.creamBorder}`,
              borderRadius: 16,
              padding: '8px 0',
              display: 'flex', flexDirection: 'column',
            }}>
              {visibleBrief.map((s, i) => (
                <div
                  key={s.id}
                  style={{
                    padding: '18px 24px',
                    borderBottom: i < visibleBrief.length - 1 ? `1px solid ${T.creamBorder}` : 'none',
                    display: 'flex', gap: 16, alignItems: 'flex-start',
                  }}
                >
                  <span style={{ fontFamily: 'DM Serif Display, serif', fontSize: 14, color: T.terra, letterSpacing: '0.05em', paddingTop: 2, minWidth: 28 }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.espresso }}>{s.title}</div>
                    <div style={{ fontSize: 14, color: T.espressoMid, lineHeight: 1.65 }}>{s.body}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty brief placeholder */}
          {visibleBrief.length === 0 && (
            <div style={{ background: T.white, border: `1px dashed ${T.creamBorder}`, borderRadius: 16, padding: '24px', textAlign: 'center', color: T.espressoLight, fontSize: 14 }}>
              Brief sections will appear here once you add content above.
            </div>
          )}

          {/* Research team */}
          {(researcherLine || study.contactEmail) && (
            <div style={{
              background: T.terraLight,
              border: `1px solid ${T.terraMid}`,
              borderRadius: 16,
              padding: '18px 22px',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.espressoLight, fontWeight: 600 }}>
                Research team
              </div>
              {researcherLine && (
                <div style={{ fontSize: 15, color: T.espresso, fontWeight: 500 }}>{researcherLine}</div>
              )}
              {study.contactEmail && (
                <div style={{ fontSize: 14, color: T.espressoMid }}>
                  Questions? Email{' '}
                  <span style={{ color: T.terraDeep, textDecoration: 'underline' }}>{study.contactEmail}</span>
                </div>
              )}
              {study.irb && (
                <div style={{ fontSize: 12, color: T.espressoLight, marginTop: 6, fontStyle: 'italic' }}>{study.irb}</div>
              )}
            </div>
          )}

          {/* Your consent */}
          <div style={{
            background: T.white,
            border: `1px solid ${T.creamBorder}`,
            borderRadius: 16,
            padding: '20px 24px',
            display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            <div style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.espressoLight, fontWeight: 600, marginBottom: 8 }}>
              Your consent
            </div>
            {[
              <>I'm <strong>18 or older</strong> and I'm taking part in this study voluntarily.</>,
              <>I've read the information above and understand that my conversation will be <strong>recorded and transcribed</strong> for research purposes.</>,
              <>I understand I can <strong>stop at any time</strong> without giving a reason, and partial responses will be handled as described above.</>,
            ].map((label, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0' }}>
                <span style={{
                  width: 22, height: 22, borderRadius: 6,
                  border: `1.5px solid ${T.creamBorder}`,
                  background: T.white,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 1,
                }} />
                <span style={{ fontSize: 14, lineHeight: 1.55, color: T.espresso }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Mic check */}
          <div style={{
            background: T.white,
            border: `1px solid ${T.creamBorder}`,
            borderRadius: 16,
            padding: '20px 24px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.espresso }}>Microphone check</div>
              <div style={{ fontSize: 13, color: T.espressoLight }}>We need your mic to hear you. Headphones recommended.</div>
            </div>
            <div style={{
              padding: '8px 16px', borderRadius: 999, fontSize: 13, fontWeight: 500,
              border: `1.5px solid ${T.creamBorder}`, color: T.espresso, background: T.white,
            }}>
              Allow microphone
            </div>
          </div>

          {/* Start button */}
          <button
            disabled
            style={{
              width: '100%', padding: '18px 36px', fontSize: 17, fontWeight: 500,
              borderRadius: 999, border: 'none',
              background: T.terra, color: T.white, opacity: 0.4,
              cursor: 'not-allowed', fontFamily: 'inherit',
            }}
          >
            I agree — start the interview
          </button>

          <p style={{ fontSize: 12, color: T.espressoLight, textAlign: 'center', lineHeight: 1.6, margin: 0 }}>
            By starting, you confirm you've read the information above and consent to take part.
          </p>
        </div>
      </div>
    </div>
  );
}
